import {
  OfficeContext,
  OfficeDocument,
  OfficeSelection,
  OfficeInsertOptions,
  WordSpecific,
  ExcelSpecific,
  PowerPointSpecific,
  OutlookSpecific,
  AttachmentItem
} from '../types'
import {
  initializeOffice,
  getSelectedText,
  insertContent,
  createOfficeError,
  isOfficeInitialized
} from '../utils/office.utils'

export abstract class OfficeService {
  protected context: OfficeContext | null = null
  protected initialized: boolean = false

  async initialize(): Promise<void> {
    if (this.initialized) return

    try {
      this.context = await initializeOffice()
      this.initialized = true
      await this.onInitialized()
    } catch (error) {
      console.error('Failed to initialize Office:', error)
      throw error
    }
  }

  protected abstract onInitialized(): Promise<void>

  isInitialized(): boolean {
    return this.initialized && isOfficeInitialized()
  }

  getContext(): OfficeContext | null {
    return this.context
  }

  async getSelection(): Promise<OfficeSelection> {
    const text = await getSelectedText()
    return {
      text,
      type: Office.CoercionType.Text,
    }
  }

  async insertText(text: string, _options?: OfficeInsertOptions): Promise<void> {
    await insertContent(text, Office.CoercionType.Text)
  }

  async insertHtml(html: string, _options?: OfficeInsertOptions): Promise<void> {
    await insertContent(html, Office.CoercionType.Html)
  }

  abstract getDocument(): Promise<OfficeDocument>
  abstract saveDocument(): Promise<void>
}

export class WordService extends OfficeService implements WordSpecific {
  protected async onInitialized(): Promise<void> {
    // Word-specific initialization
  }

  async getDocument(): Promise<OfficeDocument> {
    return Word.run(async (context) => {
      const doc = context.document
      const body = doc.body

      context.load(body, 'text')
      await context.sync()

      return {
        id: Office.context.document.url || 'unknown',
        name: 'Document',
        type: 'word',
        content: body.text,
      }
    })
  }

  async saveDocument(): Promise<void> {
    return Word.run(async (context) => {
      await context.sync()
    })
  }

  async insertParagraph(text: string, position: "End" | "Start" = "End"): Promise<void> {
    return Word.run(async (context) => {
      context.document.body.insertParagraph(text, position)
      await context.sync()
    })
  }

  async insertTable(rows: number, columns: number, values?: string[][]): Promise<void> {
    return Word.run(async (context) => {
      const table = context.document.body.insertTable(rows, columns, Word.InsertLocation.end, values)
      table.styleBuiltIn = Word.BuiltInStyleName.gridTable4_Accent1
      await context.sync()
    })
  }

  async applyStyle(styleName: string): Promise<void> {
    return Word.run(async (context) => {
      const selection = context.document.getSelection()
      selection.style = styleName
      await context.sync()
    })
  }

  async insertImage(base64: string, width?: number, height?: number): Promise<void> {
    return Word.run(async (context) => {
      const image = context.document.body.insertInlinePictureFromBase64(base64, Word.InsertLocation.end)
      if (width) image.width = width
      if (height) image.height = height
      await context.sync()
    })
  }
}

export class ExcelService extends OfficeService implements ExcelSpecific {
  protected async onInitialized(): Promise<void> {
    // Excel-specific initialization
  }

  async getDocument(): Promise<OfficeDocument> {
    return Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet()
      const range = sheet.getUsedRange()

      context.load(range, 'values')
      await context.sync()

      return {
        id: Office.context.document.url || 'unknown',
        name: 'Workbook',
        type: 'excel',
        content: range?.values || [],
      }
    })
  }

  async saveDocument(): Promise<void> {
    return Excel.run(async (context) => {
      await context.sync()
    })
  }

  async getSelectedRange(): Promise<Excel.Range> {
    return Excel.run(async (context) => {
      const range = context.workbook.getSelectedRange()
      context.load(range)
      await context.sync()
      return range
    })
  }

  async setFormula(formula: string, rangeAddress?: string): Promise<void> {
    return Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet()
      const range = rangeAddress
        ? sheet.getRange(rangeAddress)
        : context.workbook.getSelectedRange()

      range.formulas = [[formula]]
      await context.sync()
    })
  }

  async createChart(type: Excel.ChartType, data: Excel.Range): Promise<void> {
    return Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet()
      sheet.charts.add(type, data, Excel.ChartSeriesBy.auto)
      await context.sync()
    })
  }

  async applyConditionalFormat(_rules: unknown): Promise<void> {
    return Excel.run(async (context) => {
      context.workbook.getSelectedRange()
      // Apply conditional formatting rules
      await context.sync()
    })
  }
}

export class PowerPointService extends OfficeService implements PowerPointSpecific {
  protected async onInitialized(): Promise<void> {
    // PowerPoint-specific initialization
  }

  async getDocument(): Promise<OfficeDocument> {
    // PowerPoint doesn't have the same API structure
    return {
      id: Office.context.document.url || 'unknown',
      name: 'Presentation',
      type: 'powerpoint',
    }
  }

  async saveDocument(): Promise<void> {
    // PowerPoint save implementation
  }

  async insertSlide(_layout?: string): Promise<void> {
    // Insert slide implementation
  }

  async insertTextBox(text: string, _options?: unknown): Promise<void> {
    await this.insertText(text)
  }

  async insertImage(_base64: string, _options?: unknown): Promise<void> {
    // Insert image implementation
  }

  async applyTheme(_themeName: string): Promise<void> {
    // Apply theme implementation
  }
}

export class OutlookService extends OfficeService implements OutlookSpecific {
  protected async onInitialized(): Promise<void> {
    // Outlook-specific initialization
  }

  async getDocument(): Promise<OfficeDocument> {
    return {
      id: Office.context.mailbox?.item?.itemId || 'unknown',
      name: 'Email',
      type: 'outlook',
    }
  }

  async saveDocument(): Promise<void> {
    // Outlook doesn't save in the same way
  }

  async insertSignature(html: string): Promise<void> {
    return new Promise((resolve, reject) => {
      Office.context.mailbox.item?.body.setSelectedDataAsync(
        html,
        { coercionType: Office.CoercionType.Html },
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve()
          } else {
            reject(createOfficeError(result.error))
          }
        }
      )
    })
  }

  async addAttachment(item: AttachmentItem): Promise<void> {
    return new Promise((resolve, reject) => {
      Office.context.mailbox.item?.addFileAttachmentAsync(
        item.url || '',
        item.name,
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve()
          } else {
            reject(createOfficeError(result.error))
          }
        }
      )
    })
  }

  async setRecipients(to: string[], cc?: string[], bcc?: string[]): Promise<void> {
    const item = Office.context.mailbox.item
    if (!item) return

    const promises: Promise<void>[] = []

    if (to.length > 0) {
      promises.push(new Promise((resolve, reject) => {
        item.to.setAsync(to, (result) => {
          result.status === Office.AsyncResultStatus.Succeeded ? resolve() : reject(result.error)
        })
      }))
    }

    if (cc && cc.length > 0) {
      promises.push(new Promise((resolve, reject) => {
        item.cc.setAsync(cc, (result) => {
          result.status === Office.AsyncResultStatus.Succeeded ? resolve() : reject(result.error)
        })
      }))
    }

    if (bcc && bcc.length > 0) {
      promises.push(new Promise((resolve, reject) => {
        item.bcc?.setAsync(bcc, (result) => {
          result.status === Office.AsyncResultStatus.Succeeded ? resolve() : reject(result.error)
        })
      }))
    }

    await Promise.all(promises)
  }

  async scheduleEmail(_date: Date): Promise<void> {
    // Schedule email implementation
  }
}

// Factory function to create the appropriate service
export function createOfficeService(host: Office.HostType): OfficeService {
  switch (host) {
    case Office.HostType.Word:
      return new WordService()
    case Office.HostType.Excel:
      return new ExcelService()
    case Office.HostType.PowerPoint:
      return new PowerPointService()
    case Office.HostType.Outlook:
      return new OutlookService()
    default:
      throw new Error(`Unsupported Office host: ${host}`)
  }
}
